// LocalStorage cache for dashboards. Entries are keyed by user_email and live alongside
// the server-backed `/api/dashboards` records, so we always show the user their last
// viewed dashboard instantly on mount and refresh from the server in the background.

import { getActiveEntitySlug } from '../../utils/activeEntity';

const VERSION = 1;
const PREFIX = `risklo-dashboards.v${VERSION}`;

function safeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function listKey(email) {
  return `${PREFIX}.list::${safeEmail(email)}`;
}

// The org-wide dashboard ('vector-dashboard') is each brand's own board, so its
// cached copy is kept per brand. Personal dashboards belong to the user.
const ORG_DASHBOARD_ID = 'vector-dashboard';

function dashboardKey(email, dashboardId) {
  const brand = dashboardId === ORG_DASHBOARD_ID ? `::${getActiveEntitySlug() || 'vector'}` : '';
  return `${PREFIX}.dash::${safeEmail(email)}::${dashboardId}${brand}`;
}

function activeKey(email) {
  return `${PREFIX}.active::${safeEmail(email)}`;
}

function read(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function write(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_err) {
    /* localStorage may be full or unavailable — non-fatal */
  }
}

function remove(key) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch (_err) {
    /* non-fatal */
  }
}

export function readCachedDashboardList(email) {
  if (!email) return null;
  const cached = read(listKey(email));
  if (!cached || !Array.isArray(cached.dashboards)) return null;
  return cached.dashboards;
}

export function writeCachedDashboardList(email, dashboards) {
  if (!email || !Array.isArray(dashboards)) return;
  write(listKey(email), { dashboards, ts: Date.now() });
}

export function readCachedDashboard(email, dashboardId) {
  if (!email || !dashboardId) return null;
  const cached = read(dashboardKey(email, dashboardId));
  if (!cached || !cached.dashboard) return null;
  return cached.dashboard;
}

export function writeCachedDashboard(email, dashboard) {
  if (!email || !dashboard || !dashboard.id) return;
  write(dashboardKey(email, dashboard.id), { dashboard, ts: Date.now() });
}

export function clearCachedDashboard(email, dashboardId) {
  if (!email || !dashboardId) return;
  remove(dashboardKey(email, dashboardId));
}

export function readActiveDashboardId(email) {
  if (!email) return null;
  const cached = read(activeKey(email));
  return cached?.id || null;
}

export function writeActiveDashboardId(email, dashboardId) {
  if (!email) return;
  if (!dashboardId) {
    remove(activeKey(email));
    return;
  }
  write(activeKey(email), { id: dashboardId, ts: Date.now() });
}

// One-time per-user migration flag for the Vector-default rollout. Once set,
// the dashboard page won't re-run the demote-stale-personal-defaults pass.
const VECTOR_DEFAULT_MIGRATION_PREFIX = `${PREFIX}.vector-default-migration.v1`;

function vectorDefaultMigrationKey(email) {
  return `${VECTOR_DEFAULT_MIGRATION_PREFIX}::${safeEmail(email)}`;
}

export function hasMigratedToVectorDefault(email) {
  if (!email) return true;
  return !!read(vectorDefaultMigrationKey(email));
}

export function markMigratedToVectorDefault(email) {
  if (!email) return;
  write(vectorDefaultMigrationKey(email), { ts: Date.now() });
}
